import type { PublicAppointmentSession, PublicRescheduleResponse } from "@emr/contracts";
import { AddToCalendar } from "../AddToCalendar";
import { AppointmentTicket } from "./ManageAppointment";

/**
 * After a time change (PA-9): the new time, its calendar file (whose SEQUENCE is the booking's
 * new version, so a calendar updates the event it already has), and the new links, returned
 * once. The new manage link opens in this tab and asks for the date of birth again; the
 * questionnaire link opens a new tab so this page, and the new manage link, stay. Neither ever
 * enters the address bar or storage: the page strips a link's token as soon as it opens.
 */
export function RescheduledAppointment({
  response,
  session,
  physician,
}: {
  readonly response: PublicRescheduleResponse;
  /** The session the change was made from: the clinic, visit type and clock. */
  readonly session: PublicAppointmentSession;
  /** Who the new time is with; null when the clinic assigns the clinician. */
  readonly physician: string | null;
}) {
  return (
    <div className="manage-panel">
      <AppointmentTicket
        startAt={response.startAt}
        endAt={response.endAt}
        timeZone={session.timeZone}
        visit={session.appointmentTypeName}
        clinician={physician ?? "Assigned by the clinic"}
        address={session.clinicAddress}
      />
      <AddToCalendar
        appointment={{
          bookingReference: response.bookingReference,
          sequence: response.version,
          startAt: response.startAt,
          endAt: response.endAt,
          clinicName: session.clinicName,
          clinicAddress: session.clinicAddress,
        }}
      />
      <section className="manage-new-link" aria-labelledby="manage-new-link-heading">
        <h2 id="manage-new-link-heading">Your new link</h2>
        <p>
          The link you used no longer works. Use this one to confirm, cancel or change this
          appointment.
        </p>
        <a className="continue-button booking-link-button" href={response.manageAppointmentUrl}>
          Open your new appointment link
        </a>
        {response.intakeQuestionnaireUrl === undefined ? null : (
          <>
            <p>Your questionnaire has a new link too.</p>
            <a
              className="outline-button booking-link-button"
              href={response.intakeQuestionnaireUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Complete your questionnaire
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          </>
        )}
      </section>
      <p className="manage-note">{response.notice}</p>
    </div>
  );
}
